import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AlarmClock, ArrowDownUp, ArrowLeft, ArrowLeftRight, Inbox, MailOpen } from 'lucide-react';

import { useAuth } from '@/nucleo/auth/useAuth';
import { formatarDataHora } from '@/nucleo/formato/datas';
import { useUrlFilters } from '@/nucleo/ganchos/useUrlFilters';
import { ehSoDre } from '@/nucleo/navegacao/apresentacao';
import { cn } from '@/nucleo/utilitarios';
import { Badge } from '@/ui/base/badge';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { Label } from '@/ui/base/label';
import { MultiSelectCombobox } from '@/ui/base/multi-select-combobox';
import { ScrollArea } from '@/ui/base/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { Switch } from '@/ui/base/switch';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { MonthFilter } from '@/ui/MonthFilter';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { StatsGrid, type StatItem } from '@/ui/StatsGrid';

import type { ConversaDaMedicao } from './contrato';
import { ConversaDaMedicaoPainel } from './ConversaDaMedicaoPainel';
import { useCaixaDeMensagens, useDresDasMensagens } from './dados';
import { prazoDeResposta, SLA_RESPOSTA_SME_DIAS_UTEIS } from './sla';

/**
 * `/sme/mensagens` — canal entre a SME e os credenciados, uma conversa por
 * medição.
 *
 * Migrada do protótipo (`SmeMensagens.tsx`): caixa de conversas à esquerda,
 * conversa aberta à direita; abaixo de `lg`, uma coisa de cada vez, com botão
 * de voltar. O que mudou, e por quê:
 *
 * 1. A PERSONA DRE NÃO RECEBE A CAIXA. No protótipo, quem é só DRE tem o filtro
 *    travado na própria diretoria (`useDresEscopo`). Aqui nada sabe de qual
 *    diretoria é quem está logado — `UsuarioAutenticado` (`GET /eu`) não tem
 *    campo de DRE —, e o mock devolve as conversas de todas. Mesma saída de
 *    `/sme/tratativas`: a persona DRE recebe um estado que explica a lacuna, e
 *    a consulta nem roda.
 * 2. O FILTRO DE DRE FICA SEMPRE VISÍVEL para quem recebe a caixa, pelo mesmo
 *    motivo — o selo de contexto (`DreContextChip`) só existe para DRE puro.
 * 3. A COMPETÊNCIA VAI PARA A URL junto dos outros filtros; no protótipo ela
 *    era estado local e se perdia ao recarregar.
 * 4. ENVIAR E MARCAR COMO LIDA NÃO GRAVAM — ver `ConversaDaMedicaoPainel.tsx`.
 *
 * A leitura é mockada (`dados.ts`, exceção de 2026-08-22 do CLAUDE.md): a
 * tabela `medicao_mensagens` existe no banco, mas nenhuma rota a serve.
 */

const TITULO = 'Mensagens';
const DESCRICAO = 'Canal bidirecional com credenciados, organizado por medição.';

type Ordenacao = 'recentes' | 'antigas' | 'mais_msgs';

const FILTROS_PADRAO = {
  msg_busca: '',
  msg_status: '',
  msg_dre: '',
  msg_mes: '',
  msg_ord: 'recentes',
  msg_naolidas: '1',
};

const CAMPOS: FilterFieldConfig[] = [
  {
    key: 'msg_busca',
    label: 'Buscar',
    type: 'search',
    placeholder: 'Credenciado, CPF/CNPJ...',
    ariaLabel: 'Buscar por credenciado ou CPF/CNPJ',
    flex: true,
  },
  {
    key: 'msg_status',
    label: 'Situação',
    type: 'select',
    options: [
      { value: 'nao_lidas', label: 'Com não lidas' },
      { value: 'atrasadas', label: `Atrasadas SME (>${SLA_RESPOSTA_SME_DIAS_UTEIS}d úteis)` },
      { value: 'vencendo', label: 'Vencendo (≤1d útil)' },
      { value: 'respondidas', label: 'Sem pendências' },
    ],
    allOptionLabel: 'Todas',
    width: 'w-full sm:w-[220px]',
  },
];

function competenciaCurta(competencia: string): string {
  const [ano, mes] = competencia.split('-');
  return `${mes}/${ano}`;
}

function instante(iso: string): number {
  return new Date(iso).getTime();
}

export function MensagensSmePage() {
  const { usuario } = useAuth();
  const soDre = ehSoDre(usuario?.papeis);

  const { values, setFilter, clearAll } = useUrlFilters(FILTROS_PADRAO);
  const [parametros] = useSearchParams();
  const [aberta, setAberta] = useState<string | null>(null);

  const caixa = useCaixaDeMensagens({ habilitado: !soDre });
  const dres = useDresDasMensagens({ habilitado: !soDre });

  // Atalho `/sme/mensagens?dre=<id>`, vindo do painel SME: preenche o filtro uma vez.
  useEffect(() => {
    const daUrl = parametros.get('dre');
    if (daUrl && !soDre) {
      setFilter('msg_dre', daUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const naoLidasPrimeiro = values.msg_naolidas !== '0';
  const ordenacao = (values.msg_ord || 'recentes') as Ordenacao;
  const dresSelecionadas = useMemo(
    () => (values.msg_dre ? values.msg_dre.split(',').filter(Boolean) : []),
    [values.msg_dre],
  );

  const conversas = useMemo(() => caixa.data ?? [], [caixa.data]);

  const filtradas = useMemo(() => {
    const agora = new Date();
    const situacao = (c: ConversaDaMedicao) =>
      prazoDeResposta(c.ultimaMensagemEm, c.ultimoAutorPapel, agora).situacao;
    const busca = values.msg_busca.trim().toLowerCase();
    const selecionadas = new Set(dresSelecionadas);

    const lista = conversas
      .filter((c) => !values.msg_mes || c.competencia === values.msg_mes)
      .filter((c) => selecionadas.size === 0 || selecionadas.has(c.dreId))
      .filter((c) => {
        switch (values.msg_status) {
          case 'nao_lidas':
            return c.naoLidas > 0;
          case 'respondidas':
            return c.naoLidas === 0;
          case 'atrasadas':
            return situacao(c) === 'VENCIDO';
          case 'vencendo':
            return situacao(c) === 'VENCENDO';
          default:
            return true;
        }
      })
      .filter(
        (c) =>
          !busca ||
          c.credenciadoNome.toLowerCase().includes(busca) ||
          c.credenciadoDocumento.toLowerCase().includes(busca),
      );

    return lista.sort((a, b) => {
      if (naoLidasPrimeiro && a.naoLidas > 0 !== b.naoLidas > 0) {
        return a.naoLidas > 0 ? -1 : 1;
      }
      if (ordenacao === 'antigas')
        return instante(a.ultimaMensagemEm) - instante(b.ultimaMensagemEm);
      if (ordenacao === 'mais_msgs') return b.totalMensagens - a.totalMensagens;
      return instante(b.ultimaMensagemEm) - instante(a.ultimaMensagemEm);
    });
  }, [conversas, values, dresSelecionadas, naoLidasPrimeiro, ordenacao]);

  // Os indicadores contam a caixa inteira, não o recorte — como no protótipo.
  const indicadores: StatItem[] = useMemo(() => {
    const agora = new Date();
    return [
      { title: 'Conversas', value: conversas.length, icon: Inbox },
      {
        title: 'Não lidas',
        value: conversas.reduce((soma, c) => soma + c.naoLidas, 0),
        icon: MailOpen,
      },
      {
        title: 'Abertas',
        value: conversas.filter((c) => c.naoLidas > 0).length,
        icon: ArrowLeftRight,
      },
      {
        title: `Atrasadas SME (>${SLA_RESPOSTA_SME_DIAS_UTEIS}d úteis)`,
        value: conversas.filter(
          (c) =>
            prazoDeResposta(c.ultimaMensagemEm, c.ultimoAutorPapel, agora).situacao === 'VENCIDO',
        ).length,
        icon: AlarmClock,
      },
    ];
  }, [conversas]);

  const selecionada = filtradas.find((c) => c.medicaoId === aberta) ?? null;

  if (soDre) {
    return (
      <div className="space-y-6">
        <PageHeader title={TITULO} description={DESCRICAO} />
        <EmptyState
          variant="messages"
          title="As conversas da sua diretoria ainda não estão disponíveis"
          description="Mostrar só as conversas da sua diretoria depende de uma consulta que ainda está sendo construída. Até lá a caixa não é exibida — mostrar as conversas de todas as diretorias seria pior do que não mostrar nenhuma. Enquanto isso, fale com a equipe da SME."
        />
      </div>
    );
  }

  if (caixa.isError) {
    return (
      <div className="space-y-6">
        <PageHeader title={TITULO} description={DESCRICAO} />
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar as mensagens"
          primaryAction={{ label: 'Tentar de novo', onClick: () => void caixa.refetch() }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader title={TITULO} description={DESCRICAO} />

      <section aria-label="Indicadores das mensagens">
        <StatsGrid columns="grid-cols-2 lg:grid-cols-4" stats={indicadores} />
      </section>

      <FilterBar
        fields={CAMPOS}
        values={values}
        defaultValues={FILTROS_PADRAO}
        onChange={(chave, valor) => setFilter(chave as keyof typeof FILTROS_PADRAO, valor)}
        onClear={clearAll}
      >
        <MonthFilter
          id="msg-competencia"
          label="Competência"
          value={values.msg_mes}
          onChange={(competencia) => setFilter('msg_mes', competencia)}
        />
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wider text-muted-foreground">DRE</Label>
          <MultiSelectCombobox
            ariaLabel="Filtrar por DRE"
            options={(dres.data ?? []).map((d) => ({ value: d.id, label: d.nome, code: d.codigo }))}
            selected={dresSelecionadas}
            onChange={(selecao) => setFilter('msg_dre', selecao.join(','))}
            placeholder="Todas"
            searchPlaceholder="Buscar DRE..."
            emptyMessage="Nenhuma DRE encontrada."
            renderSelectedText={(n) => (n === 1 ? '1 DRE' : `${n} DREs`)}
            triggerClassName="w-full sm:w-[200px]"
          />
        </div>
      </FilterBar>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
        <Card className={cn('rounded-2xl', selecionada && 'hidden lg:block')}>
          <CardHeader className="space-y-3 pb-3">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">Conversas</CardTitle>
              <Select value={ordenacao} onValueChange={(valor) => setFilter('msg_ord', valor)}>
                <SelectTrigger
                  aria-label="Ordenar conversas"
                  className="h-8 w-auto min-w-[150px] gap-1.5 px-2.5 text-xs"
                >
                  <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  <SelectItem value="recentes">Mais recentes</SelectItem>
                  <SelectItem value="antigas">Mais antigas</SelectItem>
                  <SelectItem value="mais_msgs">Mais mensagens</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-2 rounded-md border border-border/60 bg-muted/30 px-2.5 py-1.5">
              <Label htmlFor="msg-nao-lidas-primeiro" className="cursor-pointer text-xs">
                Não lidas primeiro
              </Label>
              <Switch
                id="msg-nao-lidas-primeiro"
                checked={naoLidasPrimeiro}
                onCheckedChange={(ligado) => setFilter('msg_naolidas', ligado ? '1' : '0')}
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {caixa.isLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner />
              </div>
            ) : filtradas.length === 0 ? (
              <div className="p-6">
                <EmptyState
                  variant="messages"
                  title="Nenhuma conversa"
                  description={
                    conversas.length === 0
                      ? 'As conversas aparecem aqui quando um credenciado escrever sobre uma medição.'
                      : 'Ajuste a busca, a competência, a DRE ou a situação para ver outras conversas.'
                  }
                />
              </div>
            ) : (
              <ScrollArea className="h-[60vh] lg:h-[520px]">
                <ul className="divide-y divide-border" aria-label="Conversas">
                  {filtradas.map((conversa) => (
                    <li key={conversa.medicaoId}>
                      <ItemDaConversa
                        conversa={conversa}
                        ativa={conversa.medicaoId === aberta}
                        aoAbrir={() => setAberta(conversa.medicaoId)}
                      />
                    </li>
                  ))}
                </ul>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <div className={cn(!selecionada && 'hidden lg:block')}>
          {selecionada ? (
            <div className="space-y-3">
              <Card className="rounded-2xl">
                <CardContent className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 lg:hidden"
                      onClick={() => setAberta(null)}
                      aria-label="Voltar para conversas"
                    >
                      <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    </Button>
                    <div className="min-w-0">
                      <h2 className="truncate text-sm font-semibold">
                        {selecionada.credenciadoNome}
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        {selecionada.credenciadoDocumento} · Medição{' '}
                        {competenciaCurta(selecionada.competencia)}
                      </p>
                    </div>
                  </div>
                  <Badge variant="secondary">DRE {selecionada.dreCodigo}</Badge>
                </CardContent>
              </Card>
              <ConversaDaMedicaoPainel medicaoId={selecionada.medicaoId} />
            </div>
          ) : (
            <Card className="h-full rounded-2xl">
              <CardContent className="p-6">
                <EmptyState
                  variant="messages"
                  title="Nenhuma conversa selecionada"
                  description="Selecione uma conversa na lista para ler as mensagens."
                />
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

interface ItemDaConversaProps {
  conversa: ConversaDaMedicao;
  ativa: boolean;
  aoAbrir: () => void;
}

/** A linha inteira é o alvo do clique (regra 9 de interface) — não há botão "Ver". */
function ItemDaConversa({ conversa, ativa, aoAbrir }: ItemDaConversaProps) {
  const prazo = prazoDeResposta(conversa.ultimaMensagemEm, conversa.ultimoAutorPapel);
  const autor = conversa.ultimoAutorPapel === 'CREDENCIADO' ? 'Credenciado' : 'SME';

  return (
    <button
      type="button"
      onClick={aoAbrir}
      aria-current={ativa ? 'true' : undefined}
      className={cn(
        'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        ativa && 'bg-muted/70',
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="truncate text-sm font-medium">{conversa.credenciadoNome}</span>
        <span className="flex shrink-0 items-center gap-1">
          {prazo.situacao === 'VENCIDO' && (
            <Badge variant="destructive" className="gap-1 px-1.5">
              <AlarmClock className="h-3 w-3" aria-hidden="true" />
              {prazo.diasAtraso}d atraso
            </Badge>
          )}
          {prazo.situacao === 'VENCENDO' && (
            <Badge variant="outline" className="gap-1 border-warning px-1.5 text-warning">
              <AlarmClock className="h-3 w-3" aria-hidden="true" />
              Vencendo
            </Badge>
          )}
          {conversa.naoLidas > 0 && (
            <Badge className="px-1.5">
              {conversa.naoLidas}
              <span className="sr-only"> não lidas</span>
            </Badge>
          )}
        </span>
      </span>
      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <span>{competenciaCurta(conversa.competencia)}</span>
        <span>· DRE {conversa.dreCodigo}</span>
      </span>
      <span className="line-clamp-2 text-xs text-muted-foreground">
        <span className="font-medium">{autor}:</span> {conversa.ultimaMensagem}
      </span>
      <span className="text-xs text-muted-foreground">
        {formatarDataHora(conversa.ultimaMensagemEm)}
      </span>
    </button>
  );
}
